import { db, type MosDB, newId, type Project, type Task, type TimeBlock } from "./db";
import { nextDueDate } from "./recurrence";
import { nextUpdatedAt, remove, save, update } from "./store";

// Re-exported for callers that subscribed before store.ts existed.
export { onLocalWrite } from "./store";

/** Task priority 0-3, as stored. */
export const PRIORITY_LABELS = ["None", "Low", "Medium", "High"] as const;

export type NewTask = Partial<Omit<Task, "id" | "created_at" | "updated_at" | "deleted_at">> & {
	title: string;
};

export function createTask(input: NewTask, database: MosDB = db): Promise<Task> {
	const now = nextUpdatedAt();
	return save(
		"tasks",
		{
			id: newId(),
			project_id: null,
			parent_id: null,
			notes: "",
			status: input.due_date ? "todo" : "inbox",
			priority: 0,
			tags: [],
			due_date: null,
			due_at: null,
			estimate_min: null,
			must_win_date: null,
			recurrence_rule: null,
			completed_at: null,
			...input,
			title: input.title.trim(),
			created_at: now,
			updated_at: now,
			deleted_at: null,
		},
		database,
	);
}

export function updateTask(id: string, patch: Partial<Task>, database: MosDB = db): Promise<Task> {
	return update("tasks", id, patch, database);
}

/**
 * Soft-deletes a task and its subtasks. The server only cascades on the final
 * purge, so children are deleted explicitly to keep them from lingering.
 */
export function deleteTask(id: string, database: MosDB = db): Promise<void> {
	return database.transaction("rw", database.tasks, database.outbox, async () => {
		const children = await database.tasks.where("parent_id").equals(id).primaryKeys();
		for (const child of children) await remove("tasks", child, database);
		await remove("tasks", id, database);
	});
}

/** Adds a subtask under `parentId`; it inherits the parent's project. */
export async function createSubtask(
	parentId: string,
	title: string,
	database: MosDB = db,
): Promise<Task> {
	const parent = await database.tasks.get(parentId);
	if (!parent || parent.deleted_at) throw new Error(`tasks ${parentId} not found`);
	return createTask(
		{ title, parent_id: parentId, project_id: parent.project_id, status: "todo" },
		database,
	);
}

export type NewBlock = Pick<TimeBlock, "start_at" | "end_at"> &
	Partial<Pick<TimeBlock, "task_id" | "kind" | "title">>;

export function createBlock(input: NewBlock, database: MosDB = db): Promise<TimeBlock> {
	const now = nextUpdatedAt();
	return save(
		"time_blocks",
		{
			id: newId(),
			task_id: null,
			kind: "task",
			title: "",
			...input,
			created_at: now,
			updated_at: now,
			deleted_at: null,
		},
		database,
	);
}

export function updateBlock(
	id: string,
	patch: Partial<TimeBlock>,
	database: MosDB = db,
): Promise<TimeBlock> {
	return update("time_blocks", id, patch, database);
}

export function deleteBlock(id: string, database: MosDB = db): Promise<void> {
	return remove("time_blocks", id, database);
}

/** The most must-wins one day can have (PL-3); the API enforces it too. */
export const MAX_MUST_WINS = 3;

export class MustWinLimitError extends Error {
	constructor() {
		super(`A day has at most ${MAX_MUST_WINS} must-wins`);
		this.name = "MustWinLimitError";
	}
}

/**
 * Pins or unpins a task as one of `day`'s must-wins. Pinning also schedules
 * it for that day. The count is checked in the same transaction as the write
 * so two quick taps cannot make four.
 */
export function setMustWin(
	id: string,
	day: string,
	pinned: boolean,
	database: MosDB = db,
): Promise<Task> {
	return database.transaction("rw", database.tasks, database.outbox, async () => {
		if (pinned) {
			const others = await database.tasks
				.where("must_win_date")
				.equals(day)
				.filter((t) => !t.deleted_at && t.id !== id)
				.count();
			if (others >= MAX_MUST_WINS) throw new MustWinLimitError();
			return updateTask(id, { must_win_date: day, due_date: day, status: "todo" }, database);
		}
		return updateTask(id, { must_win_date: null }, database);
	});
}

/**
 * Ticks a task done or back to todo. Completing a recurring task creates the
 * next occurrence in the same transaction (PL-5). The rule moves to the new
 * task, so un-ticking and re-ticking the old one cannot spawn a duplicate.
 */
export function setDone(
	id: string,
	done: boolean,
	database: MosDB = db,
): Promise<{ task: Task; next: Task | null }> {
	return database.transaction("rw", database.tasks, database.outbox, async () => {
		if (!done) {
			const task = await updateTask(id, { status: "todo", completed_at: null }, database);
			return { task, next: null };
		}
		const current = await database.tasks.get(id);
		const rule = current?.recurrence_rule;
		const task = await updateTask(
			id,
			{
				status: "done",
				completed_at: new Date().toISOString(),
				...(rule ? { recurrence_rule: null } : {}),
			},
			database,
		);
		if (!current || !rule || !current.due_date) return { task, next: null };

		const dueDate = nextDueDate(rule, current.due_date);
		// Keep the same clock time; Kampala has no DST, so whole days are exact.
		const shiftMs =
			Date.parse(`${dueDate}T00:00:00Z`) - Date.parse(`${current.due_date}T00:00:00Z`);
		const next = await createTask(
			{
				title: current.title,
				notes: current.notes,
				tags: current.tags,
				priority: current.priority,
				project_id: current.project_id,
				estimate_min: current.estimate_min,
				recurrence_rule: rule,
				due_date: dueDate,
				due_at: current.due_at
					? new Date(Date.parse(current.due_at) + shiftMs).toISOString()
					: null,
				status: "todo",
			},
			database,
		);
		return { task, next };
	});
}

/**
 * Applies `fn` to the block's current stored value in one transaction, so
 * rapid taps (later, later, longer) each build on the previous result
 * instead of on whatever the screen last rendered.
 */
export function transformBlock(
	id: string,
	fn: (block: TimeBlock) => Partial<TimeBlock>,
	database: MosDB = db,
): Promise<TimeBlock> {
	return database.transaction("rw", database.time_blocks, database.outbox, async () => {
		const current = await database.time_blocks.get(id);
		if (!current || current.deleted_at) throw new Error(`time_blocks ${id} not found`);
		return updateBlock(id, fn(current), database);
	});
}

export function createProject(name: string, database: MosDB = db): Promise<Project> {
	const now = nextUpdatedAt();
	return save(
		"projects",
		{
			id: newId(),
			name: name.trim(),
			color: "",
			archived: false,
			created_at: now,
			updated_at: now,
			deleted_at: null,
		},
		database,
	);
}

/** Rename or archive. Projects are archived, not deleted, so tasks keep their label. */
export function updateProject(
	id: string,
	patch: Partial<Pick<Project, "name" | "archived" | "color">>,
	database: MosDB = db,
): Promise<Project> {
	return update("projects", id, patch, database);
}
